import type { QuestionnaireResponse } from "@medplum/fhirtypes";

import {
  ROM_CONFIRMED_BY_LINK_ID,
  ROM_CONFIRMED_BY_OTHER_LINK_ID,
  ROM_DATE_LINK_ID,
  ROM_DATE_TIME_LINK_ID,
  ROM_FLUID_COLOR_LINK_ID,
  ROM_FLUID_COLOR_OTHER_LINK_ID,
  ROM_METHOD_LINK_ID,
  ROM_NOTES_LINK_ID,
  ROM_ROOT_LINK_ID,
  ROM_SIGNATURE_INITIALS_LINK_ID,
  ROM_TIME_LINK_ID,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/rom/constants/rom-link-ids";
import type { RomFlowsheetFormValues } from "@/encounters/components/EncounterLabor/flowsheet-forms/rom/types/rom-form";
import { getEmptyRomFlowsheetFormValues } from "@/encounters/components/EncounterLabor/flowsheet-forms/rom/utils/rom-form-defaults";
import {
  parseCodingFromNativeSelect,
  readCodingAnswerAsNativeSelectValue,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/labor-flowsheet-coding-select";
import {
  findQuestionnaireResponseItem,
  parseQuestionnaireResponseLocalValueDate,
  readQuestionnaireSignatureInitials,
  readQuestionnaireStringAnswer,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/questionnaire-response-readers";

export function mapQuestionnaireResponseToRomFormValues(
  questionnaireResponse: QuestionnaireResponse | undefined,
): RomFlowsheetFormValues {
  // Use empty values so a missing or malformed response leaves date/time blank.
  const emptyValues = getEmptyRomFlowsheetFormValues();
  const root = findQuestionnaireResponseItem(questionnaireResponse?.item, ROM_ROOT_LINK_ID);
  if (!root?.item) {
    return emptyValues;
  }

  const dateTimeGroup = findQuestionnaireResponseItem(root.item, ROM_DATE_TIME_LINK_ID);
  const dateAnswer = findQuestionnaireResponseItem(dateTimeGroup?.item, ROM_DATE_LINK_ID)
    ?.answer?.[0]?.valueDate;
  const timeAnswer = findQuestionnaireResponseItem(dateTimeGroup?.item, ROM_TIME_LINK_ID)
    ?.answer?.[0]?.valueTime;

  return {
    ...emptyValues,
    examDate: parseQuestionnaireResponseLocalValueDate(dateAnswer),
    examTime: timeAnswer ?? null,
    method: readCodingAnswerAsNativeSelectValue(
      findQuestionnaireResponseItem(root.item, ROM_METHOD_LINK_ID),
    ),
    confirmedBy: readCodingAnswerAsNativeSelectValue(
      findQuestionnaireResponseItem(root.item, ROM_CONFIRMED_BY_LINK_ID),
    ),
    confirmedByOther: readQuestionnaireStringAnswer(
      findQuestionnaireResponseItem(root.item, ROM_CONFIRMED_BY_OTHER_LINK_ID),
    ),
    fluidColor: readCodingAnswerAsNativeSelectValue(
      findQuestionnaireResponseItem(root.item, ROM_FLUID_COLOR_LINK_ID),
    ),
    fluidColorOther: readQuestionnaireStringAnswer(
      findQuestionnaireResponseItem(root.item, ROM_FLUID_COLOR_OTHER_LINK_ID),
    ),
    notes: readQuestionnaireStringAnswer(
      findQuestionnaireResponseItem(root.item, ROM_NOTES_LINK_ID),
    ),
    signatureInitials: readQuestionnaireSignatureInitials(
      findQuestionnaireResponseItem(root.item, ROM_SIGNATURE_INITIALS_LINK_ID),
    ),
  };
}

/** Nested form shape expected by `getQuestionnaireResponseFromFormValues` and labor bots. */
export function mapRomFormValuesToQuestionnaireValues(
  formValues: RomFlowsheetFormValues,
): Record<string, unknown> {
  return {
    [ROM_ROOT_LINK_ID]: {
      [ROM_DATE_TIME_LINK_ID]: {
        [ROM_DATE_LINK_ID]: formValues.examDate,
        [ROM_TIME_LINK_ID]: formValues.examTime,
      },
      [ROM_METHOD_LINK_ID]: parseCodingFromNativeSelect(formValues.method),
      [ROM_CONFIRMED_BY_LINK_ID]: parseCodingFromNativeSelect(formValues.confirmedBy),
      [ROM_CONFIRMED_BY_OTHER_LINK_ID]: formValues.confirmedByOther,
      [ROM_FLUID_COLOR_LINK_ID]: parseCodingFromNativeSelect(formValues.fluidColor),
      [ROM_FLUID_COLOR_OTHER_LINK_ID]: formValues.fluidColorOther,
      [ROM_NOTES_LINK_ID]: formValues.notes,
      [ROM_SIGNATURE_INITIALS_LINK_ID]: formValues.signatureInitials,
    },
  };
}
